import React from 'react';
import { AlertTriangle, TrendingUp, CheckCircle } from 'lucide-react';

const CapacityBar = ({ 
  current = 0, 
  total = 40, 
  showLabel = true,
  showPercentage = true,
  height = 'h-3',
  className = '',
  colorThresholds = {
    low: 60,      // Green below 60%
    medium: 80,   // Yellow 60-80%
    high: 100     // Red above 80%
  }
}) => {
  // Calculate percentage
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  
  // Determine color based on percentage
  const getBarColor = () => {
    if (percentage >= colorThresholds.high) return 'red';
    if (percentage >= colorThresholds.medium) return 'yellow';
    if (percentage <= colorThresholds.low) return 'green';
    return 'blue';
  };
  
  const color = getBarColor();
  
  // Color classes
  const colorClasses = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-600',
    blue: 'bg-blue-500'
  };
  
  const bgColorClasses = {
    green: 'bg-green-100',
    yellow: 'bg-yellow-100',
    red: 'bg-red-100',
    blue: 'bg-blue-100'
  };
  
  const textColorClasses = {
    green: 'text-green-700',
    yellow: 'text-yellow-700',
    red: 'text-red-700',
    blue: 'text-blue-700'
  };

  // Get icon based on status
  const getStatusIcon = () => {
    if (percentage >= colorThresholds.high) {
      return <AlertTriangle size={16} className="text-red-600" />;
    }
    if (percentage >= colorThresholds.medium) {
      return <TrendingUp size={16} className="text-yellow-600" />;
    }
    return <CheckCircle size={16} className="text-green-600" />;
  };

  return (
    <div className={`w-full ${className}`}>
      {showLabel && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            {getStatusIcon()}
            <span className="text-sm font-medium text-gray-700">
              {current} / {total} שעות
            </span>
          </div>
          {showPercentage && (
            <span className={`text-sm font-medium ${textColorClasses[color]}`}>
              {percentage}%
            </span>
          )}
        </div>
      )}
      
      <div className={`w-full bg-gray-200 rounded-full ${height} overflow-hidden`}>
        <div 
          className={`${height} rounded-full transition-all duration-300 ${colorClasses[color]}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      
      {/* Warning message for overload */}
      {percentage > 100 && (
        <div className="mt-2 flex items-center gap-1 text-xs text-red-600">
          <AlertTriangle size={14} />
          <span>עומס יתר: {percentage - 100}% מעל הקיבולת</span>
        </div>
      )}
    </div>
  );
};

// Mini version for compact displays
export const CapacityBarMini = ({ current = 0, total = 40, className = '' }) => {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  
  const getBarColor = () => {
    if (percentage >= 100) return 'bg-red-600';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="w-24 bg-gray-200 rounded-full h-2">
        <div 
          className={`h-2 rounded-full transition-all duration-300 ${getBarColor()}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      <span className="text-xs text-gray-600">{percentage}%</span>
    </div>
  );
};

// Stacked capacity bar for showing multiple users
export const StackedCapacityBar = ({ 
  users = [], 
  totalCapacity = 160,
  height = 'h-4',
  className = '' 
}) => {
  // Colors for different users
  const userColors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-yellow-500',
    'bg-pink-500',
    'bg-indigo-500'
  ];

  const totalUsed = users.reduce((sum, user) => sum + (user.used || 0), 0);
  const totalPercentage = totalCapacity > 0 ? Math.round((totalUsed / totalCapacity) * 100) : 0;

  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">
          קיבולת כוללת: {totalUsed} / {totalCapacity} שעות
        </span>
        <span className="text-sm font-medium text-gray-600">
          {totalPercentage}%
        </span>
      </div>
      
      <div className={`w-full bg-gray-200 rounded-full ${height} overflow-hidden flex`}>
        {users.map((user, index) => {
          const userPercentage = totalCapacity > 0 
            ? (user.used / totalCapacity) * 100 
            : 0;
          
          return (
            <div
              key={user.id || index}
              className={`${height} ${userColors[index % userColors.length]} transition-all duration-300`}
              style={{ width: `${userPercentage}%` }}
              title={`${user.name}: ${user.used} שעות`}
            />
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="mt-2 flex flex-wrap gap-3">
        {users.map((user, index) => (
          <div key={user.id || index} className="flex items-center gap-1">
            <div 
              className={`w-3 h-3 rounded-full ${userColors[index % userColors.length]}`} 
            />
            <span className="text-xs text-gray-600">
              {user.name} ({user.used}ש')
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Usage examples:
/*
// Basic usage
<CapacityBar current={30} total={40} />

// Without labels
<CapacityBar current={25} total={40} showLabel={false} />

// Custom height
<CapacityBar current={35} total={40} height="h-6" />

// Custom thresholds
<CapacityBar 
  current={45} 
  total={40} 
  colorThresholds={{ low: 70, medium: 90, high: 100 }}
/>

// Mini version
<CapacityBarMini current={32} total={40} />

// Stacked version for team view
<StackedCapacityBar 
  users={[
    { id: '1', name: 'יוסי', used: 35 },
    { id: '2', name: 'דנה', used: 28 },
    { id: '3', name: 'מיכל', used: 40 }
  ]}
  totalCapacity={160}
/>
*/

export default CapacityBar;